import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Season } from '@/types/classTypes';
import { format, parse } from 'date-fns';
import { formatDateForForm, toISODateString } from '@/utils/dateUtils';

const DATE_FORMAT = "MM/dd/yyyy";

interface SeasonFormData {
  name: string;
  start_date: string;
  end_date: string;
  registration_start: string;
  registration_end: string;
  registration_fee_enabled: boolean;
  tuition_prepay_enabled: boolean;
  is_active: boolean;
}

interface UseSeasonFormProps {
  season: Season | null;
  onUpdated: (seasonId: string, updatedData: Partial<Season>) => void;
}

export const useSeasonForm = ({ season, onUpdated }: UseSeasonFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<SeasonFormData>({
    name: season?.name || '',
    start_date: season?.start_date ? formatDateForForm(season.start_date, DATE_FORMAT) : '',
    end_date: season?.end_date ? formatDateForForm(season.end_date, DATE_FORMAT) : '',
    registration_start: season?.registration_start ? formatDateForForm(season.registration_start, DATE_FORMAT) : '',
    registration_end: season?.registration_end ? formatDateForForm(season.registration_end, DATE_FORMAT) : '',
    registration_fee_enabled: season?.registration_fee_enabled || false,
    tuition_prepay_enabled: season?.tuition_prepay_enabled || false,
    is_active: season?.is_active || false,
  });

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSwitchChange = useCallback((name: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  }, []);

  const handleDateChange = useCallback((name: string, date: Date | undefined) => {
    if (date) {
      const formattedDate = format(date, DATE_FORMAT);
      setFormData(prev => ({
        ...prev,
        [name]: formattedDate
      }));
    }
  }, []);

  const validateDates = useCallback(() => {
    try {
      const startDate = parse(formData.start_date, DATE_FORMAT, new Date());
      const endDate = parse(formData.end_date, DATE_FORMAT, new Date());
      const regStartDate = parse(formData.registration_start, DATE_FORMAT, new Date());
      const regEndDate = parse(formData.registration_end, DATE_FORMAT, new Date());

      if (endDate <= startDate) {
        toast.error('End date must be after start date');
        return false;
      }

      if (regEndDate <= regStartDate) {
        toast.error('Registration end must be after registration start');
        return false;
      }

      return true;
    } catch (error) {
      toast.error('Invalid date format. Please use MM/DD/YYYY');
      return false;
    }
  }, [formData]);

  const saveSeason = useCallback(async () => {
    if (!season) return;
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Season name is required');
      return;
    }

    if (!validateDates()) {
      return;
    }
    
    try {
      setIsSubmitting(true);

      // Format dates for API - use ISO date string format (YYYY-MM-DD) to avoid timezone issues
      const updateData: Partial<Season> = {
        name: formData.name,
        start_date: toISODateString(formData.start_date, DATE_FORMAT),
        end_date: toISODateString(formData.end_date, DATE_FORMAT),
        registration_start: toISODateString(formData.registration_start, DATE_FORMAT),
        registration_end: toISODateString(formData.registration_end, DATE_FORMAT),
        registration_fee_enabled: formData.registration_fee_enabled,
        tuition_prepay_enabled: formData.tuition_prepay_enabled,
        is_active: formData.is_active,
      };

      // Check for actual changes
      let hasChanges = false;
      const fieldsToCheck: (keyof Season)[] = [
        'name', 'start_date', 'end_date', 
        'registration_start', 'registration_end',
        'registration_fee_enabled', 'tuition_prepay_enabled', 'is_active'
      ];

      for (const field of fieldsToCheck) {
        if (field.includes('date')) {
          // For date fields, compare as ISO date strings (YYYY-MM-DD)
          try {
            const originalDate = season[field] ? toISODateString(season[field] as string) : '';
            const newDate = updateData[field] as string || '';
            if (originalDate !== newDate) {
              hasChanges = true;
              break;
            }
          } catch (error) {
            console.warn(`Error comparing date field ${field}:`, error);
            // If date comparison fails, assume there are changes to be safe
            hasChanges = true;
            break;
          }
        } else {
          if (season[field] !== updateData[field]) {
            hasChanges = true;
            break;
          }
        }
      }

      if (!hasChanges) {
        toast.info('No changes to save');
        return;
      }

      // Call the provided onUpdated function
      onUpdated(season.id, updateData);
      toast.success('Season updated successfully');
      
    } catch (error) {
      console.error('Error updating season:', error);
      toast.error('Failed to update season', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, season, onUpdated, validateDates]);

  return {
    formData,
    isSubmitting,
    handleChange,
    handleSwitchChange,
    handleDateChange,
    saveSeason
  };
};